/**
 * MONSTER TOWN — TYPED EVENT BUS. FROZEN CONTRACT (src/core/*). Additive only.
 *
 * THE ONLY WAY CREWS TALK TO EACH OTHER. Do not import another crew's module to
 * call into it; emit an event and let them subscribe.
 *
 * HOW A CREW ADDS ITS OWN EVENTS (this is the additive escape hatch — use it,
 * do NOT edit `GameEvents` below):
 *
 *   // src/venue/events.ts  (owned by the venue crew)
 *   import type { CustomerOrder } from '../core/types'
 *   declare module '../core/events' {
 *     interface GameEvents {
 *       'venue:orderReady': { order: CustomerOrder }
 *     }
 *   }
 *   export {}
 *
 * Import that file once from your crew's entry point and the whole game sees
 * the new event, fully typed, with zero edits to core.
 *
 * NAMING: 'domain:verbPastTense' or 'domain:noun'. Always lowerCamel after ':'.
 */

import type { Cell, Placement, Rot } from './grid'
import type {
  GameMode,
  InstanceId,
  NpcId,
  Phase,
  PlaceableId,
  SaveSlot,
  SceneId,
  Vec3,
} from './types'

// ---------------------------------------------------------------------------
// core event map — crews EXTEND this via declaration merging, never edit it
// ---------------------------------------------------------------------------

/** Canvas-space pointer sample. Always produced via `canvasNDC()`. */
export interface InputPoint {
  x: number
  y: number
  ndcX: number
  ndcY: number
}

/**
 * A tap/hold/drag already resolved against the active scene by the input
 * router: which ground point, which cell, which placed instance (if any).
 * Subscribe to these instead of touching the DOM.
 */
export interface ResolvedInput {
  point: InputPoint
  mode: GameMode
  sceneId: SceneId
  /** World point on the ground plane, or null if the ray missed. */
  ground: Vec3 | null
  /** Grid cell under the finger at level 0, or null. */
  cell: Cell | null
  /** Placed instance under the finger, or null. Tolerant: may be approximate. */
  instanceId: InstanceId | null
}

export interface GameEvents {
  // -- lifecycle
  'app:booted': { ms: number }
  'app:phaseChanged': { phase: Phase; previous: Phase }
  'app:modeChanged': { mode: GameMode; previous: GameMode }

  // -- scenes / transitions (save writes are BLOCKED between start and end)
  'scene:transitionStart': { from: SceneId | null; to: SceneId }
  'scene:transitionEnd': { scene: SceneId }
  'scene:entered': { scene: SceneId }
  'scene:left': { scene: SceneId }
  /**
   * WHERE THE PLAYER SHOULD BE STANDING now that `scene` is active. Emitted by
   * `WorldGraph.transitionTo` immediately after `scene:entered`, always — with
   * the portal's `toCell` when one was given, otherwise `null`.
   *
   * ADDED BY THE INTEGRATOR, and it closes a real hole: `transitionTo` has
   * always ACCEPTED a `spawnCell` (and `usePortalAt` has always passed the
   * portal's `toCell`) but nothing ever read it, so the player kept the world
   * coordinates it had in the PREVIOUS scene. Interiors do not share an origin —
   * the ice cream shop's grid starts at world (-19.5, 23.5) — so walking in
   * through the front door left the monster standing outside the room entirely,
   * and since the camera follows the monster, the kid arrived to an empty sky.
   * The house only escaped it because its grid happens to sit near (0,0).
   *
   * The monster crew consumes this. `spawn: null` means "no portal said where",
   * which is the cue to check the player is inside the new grid at all.
   */
  'scene:spawnAt': { scene: SceneId; spawn: Cell | null }

  // -- resolved input (emitted by src/app/router.ts — the ONE tap dispatcher)
  'input:tap': ResolvedInput
  'input:hold': ResolvedInput
  'input:dragStart': ResolvedInput
  'input:dragMove': ResolvedInput
  'input:dragEnd': ResolvedInput
  'input:pinch': { scale: number }

  // -- building
  'build:selected': { defId: PlaceableId | null }
  'build:ghostMoved': { anchor: Cell; rot: Rot; valid: boolean }
  'build:placed': { placement: Placement; sceneId: SceneId; cost: number }
  'build:removed': { instanceId: InstanceId; defId: PlaceableId; sceneId: SceneId; refund: number }
  'build:moved': { instanceId: InstanceId; from: Cell; to: Cell; rot: Rot }
  'build:rejected': { reason: string; anchor: Cell }
  'build:undo': { steps: number }
  /** Emitted after occupy/release so art crews rebuild connect-aware pieces. */
  'build:artStale': { sceneId: SceneId; placements: Placement[] }

  // -- play mode
  'play:walkTo': { sceneId: SceneId; target: Vec3 }
  'play:arrived': { sceneId: SceneId; at: Vec3 }
  /** Fired the moment a tap resolves to a placed object, BEFORE walking to it. */
  'play:interactRequested': { instanceId: InstanceId; sceneId: SceneId }
  /** Fired after the monster walked over and the interaction handler ran. */
  'play:interacted': { instanceId: InstanceId; defId: PlaceableId; sceneId: SceneId }

  // -- interaction results (fanned out by src/app/interactions.ts from the
  //    InteractionResult a placeable's `interact` returned — LAW #1's runtime).
  'prop:anim': { instanceId: InstanceId; clip: string; seconds: number; target?: string }
  'prop:toggle': { instanceId: InstanceId; on: boolean; target?: string }
  'fx:play': { effect: string; at: Vec3; count: number }
  'monster:action': { action: string; seconds: number }
  'spawn:request': { what: string; at: Vec3 }

  // -- economy
  'economy:earned': { amount: number; source: string; at?: Vec3 }
  'economy:spent': { amount: number; on: PlaceableId }
  'economy:rejected': { need: number; have: number; on: PlaceableId }
  'economy:unlocked': { tier: string; grants: PlaceableId[] }

  // -- npcs / town life
  'npc:spawned': { npcId: NpcId; sceneId: SceneId }
  'npc:despawned': { npcId: NpcId }
  'npc:reacted': { npcId: NpcId; to: InstanceId; mood: string }

  // -- save
  'save:requested': { reason: string }
  'save:committed': { slot: SaveSlot; bytes: number; ms: number }
  'save:failed': { slot: SaveSlot; error: string }
  'save:loaded': { slot: SaveSlot; version: number; recoveredFromBackup: boolean }
  'save:slotChanged': { slot: SaveSlot }

  // -- audio (the audio crew owns the implementation, everyone owns the calls)
  'audio:cue': { cue: string; at?: Vec3; volume?: number }
  'audio:voice': { key: string }
  'audio:music': { track: string | null }

  // -- ui
  'ui:toast': { icon: string; ms?: number }
  'ui:panel': { panel: string | null }
  'ui:cameraRotate': { rot: Rot }
  'ui:cameraZoom': { level: number }
}

export type EventName = keyof GameEvents
export type Handler<K extends EventName> = (payload: GameEvents[K]) => void

// ---------------------------------------------------------------------------
// bus
// ---------------------------------------------------------------------------

export class EventBus {
  private readonly map = new Map<string, Set<(p: never) => void>>()
  /** Set true to console.log every emit. Harness flips this. */
  debug = false

  on<K extends EventName>(name: K, fn: Handler<K>): () => void {
    let set = this.map.get(name)
    if (set === undefined) {
      set = new Set()
      this.map.set(name, set)
    }
    set.add(fn as (p: never) => void)
    return () => this.off(name, fn)
  }

  once<K extends EventName>(name: K, fn: Handler<K>): () => void {
    const off = this.on(name, (p) => {
      off()
      fn(p)
    })
    return off
  }

  off<K extends EventName>(name: K, fn: Handler<K>): void {
    this.map.get(name)?.delete(fn as (p: never) => void)
  }

  emit<K extends EventName>(name: K, payload: GameEvents[K]): void {
    if (this.debug) console.log('[bus]', name, payload)
    const set = this.map.get(name)
    if (set === undefined) return
    // Copy so handlers may unsubscribe during dispatch.
    for (const fn of [...set]) {
      try {
        ;(fn as Handler<K>)(payload)
      } catch (err) {
        // A broken listener must never take down the game a kid is playing.
        console.error(`[bus] handler for "${String(name)}" threw`, err)
      }
    }
  }

  clear(): void {
    this.map.clear()
  }

  listenerCount(name: EventName): number {
    return this.map.get(name)?.size ?? 0
  }
}

/** The one bus. Import this, do not construct your own. */
export const bus = new EventBus()
